import Icon, {
  ArrowRightOutlined,
  EyeInvisibleOutlined,
  FilterOutlined,
  LoadingOutlined,
  SearchOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Modal, Spin, Table, Tooltip } from 'antd';
import LinkOff from 'assets/link_off.svg?react';
import NotFoundIcon from 'assets/no_equipment_found.svg?react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfoIcon from 'assets/info.svg?react';

import type { ColumnsType } from 'antd/es/table';
import { Button, notification, Tag } from 'syngenta-digital-cropwise-react-ui-kit';
import { ActiveIntegrationOption, MatchingHistoryItem } from '../types';

import { useMatchingHistoryStore } from '../store/useMatchingHistoryStore';
import { CustomFilterDropdown } from './CustomFilterDropdown';

import { FilterDropdownProps, SorterResult } from 'antd/lib/table/interface';
import dayjs from 'dayjs';
import {
  AmplitudeEventEnum,
  AmplitudePropertyKeyEnum,
  useAmplitude,
} from 'services/amplitudeHelper';
import { CustomSearchDropdown } from './CustomSearchDropdown';
import './MatchingHistoryModal.less';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getBooleanFeatureFlagValues } from 'tools/feature-flags/launchdarkly-flags';
import { unmatchProduct } from '../api/unmatchProduct';
import { useResourceMapperStore } from '../store/useResourceMapperStore';

interface MatchingHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SearchIconProps {
  search: Record<string, any>;
  field: string;
}
const SearchIcon = ({ field, search }: SearchIconProps) => (
  <SearchOutlined style={{ color: search?.[field]?.length ? '#1890ff' : undefined }} />
);

interface SearchFilterProps {
  searchSourceProductName: string;
  setSearchSourceProductName: (value: string) => void;
  close: FilterDropdownProps['close'];
  handleApply: (
    value: string | (string | number)[] | undefined,
    trackPayload: Record<string, string>,
    operation: string,
    field: string,
  ) => void;
  amplitudeFieldDescription: string;
  field: string;
}

const SearchFilter = ({
  close,
  searchSourceProductName,
  setSearchSourceProductName,
  handleApply,
  field,
  amplitudeFieldDescription,
}: SearchFilterProps) => {
  const apply = (value?: string) => {
    handleApply(
      value,
      { [AmplitudePropertyKeyEnum.FILTER_ACTION]: amplitudeFieldDescription },
      'search',
      field,
    );
    close();
  };
  return (
    <CustomSearchDropdown
      value={searchSourceProductName}
      setValue={setSearchSourceProductName}
      clearValue={() => {
        setSearchSourceProductName('');
        apply();
      }}
      onApply={(value) => {
        apply(value);
      }}
    />
  );
};

interface FilterIconProps {
  filters: Record<string, any>;
  field: string;
}
const FilterIcon = ({ field, filters }: FilterIconProps) => (
  <FilterOutlined style={{ color: filters?.[field]?.length ? '#1890ff' : undefined }} />
);

interface FilterProps {
  filterDropdown: FilterDropdownProps;
  handleApply: (
    value: string | (string | number)[] | undefined,
    trackPayload: Record<string, string>,
    operation: string,
    field: string,
  ) => void;
  amplitudeFieldDescription: string;
  field: string;
}

const Filter = ({ filterDropdown, handleApply, field, amplitudeFieldDescription }: FilterProps) => {
  const apply = (value?: string | (string | number)[]) => {
    handleApply(
      value,
      {
        [AmplitudePropertyKeyEnum.FILTER_ACTION]: amplitudeFieldDescription,
      },
      'filters',
      field,
    );
    filterDropdown.close();
  };
  return (
    <CustomFilterDropdown
      filters={filterDropdown.filters}
      clearFilters={() => {
        filterDropdown.clearFilters?.();
        apply();
      }}
      selectedKeys={filterDropdown.selectedKeys}
      setSelectedKeys={filterDropdown.setSelectedKeys}
      onApply={(value) => {
        apply(value);
      }}
    />
  );
};

const MatchingHistoryModal = ({ isOpen, onClose }: MatchingHistoryModalProps) => {
  const flags = useFlags();
  const { enabledMatchingHistoryIgnoredFilter } = getBooleanFeatureFlagValues(flags);
  const { t } = useTranslation();
  const {
    data,
    fetchData,
    isLoading,
    hasMore,
    loadMore,
    activeIntegrations,
    search,
    filters,
    removeRowId,
  } = useMatchingHistoryStore();
  const { orgId } = useResourceMapperStore();

  const [searchSourceProductName, setSearchSourceProductName] = useState(
    search?.['source_product'] || '',
  );
  const [searchCropwiseProductName, setSearchCropwiseProductName] = useState(
    search?.['cropwise_product'] || '',
  );

  const trackEvent = useAmplitude();

  useEffect(() => {
    if (!isOpen) return;
    void fetchData(orgId);
  }, [fetchData, isOpen, orgId]);

  const reload = useCallback(
    ({ filters, sort, search }) => {
      void fetchData(orgId, {
        filters: filters?.field
          ? { [filters.field]: filters?.value?.length ? filters.value : undefined }
          : undefined,
        search: search?.field
          ? { [search.field]: search?.value?.length ? search.value : undefined }
          : undefined,
        sort,
      });
    },
    [fetchData, orgId],
  );

  const handleUnmatchConfirmation = useCallback(
    (id: string, productName: string, operationIds: string[]) => {
      trackEvent(AmplitudeEventEnum.UNMATCH_PRODUCT);
      Modal.confirm({
        title: t('resource-mapper.unmatch_confirmation_title'),
        content: t('resource-mapper.unmatch_confirmation_content', {
          operationCount: operationIds.length,
        }),
        okText: t('resource-mapper.unmatch_confirmation_ok'),
        okType: 'danger',
        cancelText: t('resource-mapper.unmatch_confirmation_cancel'),
        icon: <WarningOutlined style={{ color: '#CF3537' }} />,
        okButtonProps: { type: 'primary', style: { backgroundColor: '#CF3537' } },
        cancelButtonProps: { type: 'default' },
        className: 'unmatch-confirmation-modal',
        onOk: async () => {
          const result = await unmatchProduct({
            org_id: orgId,
            mapping_id: id,
            operation_ids: operationIds,
          });
          if (result.hasError) {
            await fetchData(orgId);
            notification.error({
              message: t('resource-mapper.unmatch_confirmation_error'),
              description: t('resource-mapper.unmatch_confirmation_error_description'),
            });
            return;
          }
          removeRowId(id);
          notification.success({
            message: t('resource-mapper.unmatch_confirmation_success', { productName }),
            description: '',
          });
        },
      });
    },
    [t, removeRowId, trackEvent, orgId, fetchData],
  );

  const handleApply = useCallback(
    (
      value: string | (string | number)[] | undefined,
      trackPayload: Record<string, string>,
      operation: string,
      field: string,
    ) => {
      trackEvent(AmplitudeEventEnum.FILTER_MATCHING_HISTORY, trackPayload);
      reload({ [operation]: { field, value } });
    },
    [reload, trackEvent],
  );

  const emptyOrNotFoundComponent = useMemo(() => {
    const isNotFound =
      Object.values(search).some((value) => value?.length) ||
      Object.values(filters).some((value) => value?.length);
    const text = isNotFound
      ? t('resource-mapper.no_matching_history_found')
      : t('resource-mapper.empty_matching_history');

    return (
      <div className="height-100% min-h-400px flex flex-col justify-center items-center">
        <NotFoundIcon width={200} height={200} />
        <span className="text-16 font-semibold text-brand-syngenta-navy">{text}</span>
        {isNotFound ? (
          <p className="text-14 font-normal text-neutral-100 mt-2">
            {t('resource-mapper.search_again')}
          </p>
        ) : (
          <Button ghost onClick={onClose} className="mt-2">
            {t('resource-mapper.go_back_and_close')}
          </Button>
        )}
      </div>
    );
  }, [search, filters, t, onClose]);

  const productTypeOptions = [
    { text: t('resource-mapper.product_type_fertilizer'), value: 'FERTILIZER' },
    { text: t('resource-mapper.product_type_seeds'), value: 'SEED' },
    { text: t('resource-mapper.product_type_crop_protection'), value: 'CROPPROTECTION' },
    { text: t('resource-mapper.product_type_service'), value: 'SERVICE' },
    { text: t('resource-mapper.product_type_mix'), value: 'MIX' },
    { text: t('resource-mapper.product_type_unknown'), value: 'UNKNOWN' },
    { text: t('resource-mapper.product_type_null'), value: 'NULL' },
  ];

  const columns: ColumnsType<MatchingHistoryItem> = [
    {
      title: t('resource-mapper.imported_product'),
      dataIndex: 'source_product_name',
      key: 'source_product',
      align: 'left',
      ellipsis: {
        showTitle: true,
      },
      sorter: true,
      defaultSortOrder: 'ascend',
      filterIcon: () => <SearchIcon search={search} field="source_product" />,
      filterDropdown: ({ close }) => (
        <SearchFilter
          searchSourceProductName={searchSourceProductName}
          setSearchSourceProductName={setSearchSourceProductName}
          close={close}
          handleApply={handleApply}
          amplitudeFieldDescription="search imported product name"
          field="source_product"
        />
      ),
    },
    {
      title: t('resource-mapper.status'),
      dataIndex: 'matched_status',
      align: 'center',
      width: '90px',
      key: 'matched_status',
      render: (value: string) => {
        if (value === 'matched') {
          return (
            <Tooltip placement="top" title={t('resource-mapper.matched')}>
              <ArrowRightOutlined />
            </Tooltip>
          );
        }

        return (
          <Tooltip placement="top" title={t('resource-mapper.ignored')}>
            <EyeInvisibleOutlined />
          </Tooltip>
        );
      },
      ...(enabledMatchingHistoryIgnoredFilter
        ? {
            filters: [
              { text: t('Matched Products'), value: 'matched' },
              { text: t('Ignored Products'), value: 'ignored' },
            ],
            filterIcon: () => <FilterIcon filters={filters} field="status" />,
            filterDropdown: (filterDropdown) => (
              <Filter
                filterDropdown={filterDropdown}
                handleApply={handleApply}
                field="status"
                amplitudeFieldDescription="filter status"
              />
            ),
          }
        : {}),
    },
    {
      title: t('resource-mapper.cropwise_product'),
      align: 'left',
      ellipsis: true,
      dataIndex: 'cropwise_product_name',
      key: 'cropwise_product',
      sorter: true,
      filterIcon: () => <SearchIcon search={search} field="cropwise_product" />,
      filterDropdown: ({ close }) => (
        <SearchFilter
          searchSourceProductName={searchCropwiseProductName}
          setSearchSourceProductName={setSearchCropwiseProductName}
          close={close}
          handleApply={handleApply}
          amplitudeFieldDescription="search cropwise product name"
          field="cropwise_product"
        />
      ),
    },
    {
      title: t('resource-mapper.integration'),
      dataIndex: 'integration_id',
      align: 'left',
      key: 'integration_id',
      width: '150px',
      ellipsis: {
        showTitle: true,
      },
      render: (value: string) => (
        <span className="text-14 text-neutral-100">
          {activeIntegrations?.find(
            (integration: ActiveIntegrationOption) => integration.integrationId === value,
          )?.integrationName ?? 'unknown'}
        </span>
      ),
      filters: activeIntegrations?.map((integration: ActiveIntegrationOption) => ({
        text: integration.integrationName,
        value: integration.integrationId,
      })),

      filterIcon: () => <FilterIcon filters={filters} field="integration_id" />,
      filterDropdown: (filterDropdown) => (
        <Filter
          filterDropdown={filterDropdown}
          handleApply={handleApply}
          field="integration_id"
          amplitudeFieldDescription="filter by integration"
        />
      ),
    },
    {
      title: t('resource-mapper.type'),
      dataIndex: 'product_type',
      width: '150px',
      align: 'left',
      key: 'product_type',
      render: (value: string) => (
        <Tag color="#F3F4F6" className="p-2 capitalize">
          <span className="text-12 text-neutral-60">
            {productTypeOptions.find((productType) => productType.value === value)?.text ||
              t('resource-mapper.product_type_unknown')}
          </span>
        </Tag>
      ),
      filters: productTypeOptions,
      filterIcon: () => <FilterIcon filters={filters} field="product_type" />,
      filterDropdown: (filterDropdown) => (
        <Filter
          filterDropdown={filterDropdown}
          handleApply={handleApply}
          field="product_type"
          amplitudeFieldDescription="filter by type"
        />
      ),
    },
    {
      title: (
        <div className="flex flex-col">
          <span className="ant-table-column-title">{t('resource-mapper.date')}</span>
          <p className="text-12 text-neutral-60">{t('resource-mapper.date_description')}</p>
        </div>
      ),
      dataIndex: 'last_modified_date',
      align: 'left',
      width: '150px',
      key: 'last_modified_date',
      ellipsis: true,
      sorter: true,
      render: (date: string) => dayjs(date).format('DD MMM YYYY'),
    },
    {
      title: (
        <div className="flex">
          <span className="ant-table-column-title">{t('resource-mapper.operations')}</span>
          <Tooltip placement="top" title={t('resource-mapper.operation_count_info')}>
            <InfoIcon className="data-matching-info-icon" />
          </Tooltip>
        </div>
      ),
      dataIndex: 'operation_list',
      align: 'center',
      width: '132px',
      key: 'operation_list',
      render: (list: Array<{ operation_id: string }>) => list?.length ?? 0,
    },
    {
      title: t('resource-mapper.action'),
      dataIndex: 'action',
      align: 'center',
      width: '150px',
      key: 'action',
      render: (_, row) => (
        <Tooltip placement="top" title={t('resource-mapper.unmatch_button_title')}>
          <Button
            onClick={() =>
              handleUnmatchConfirmation(row.mapping_id, row.source_product_name, row.operation_list)
            }
            icon={<Icon component={LinkOff} />}
            data-cy="unmatch-button"
            data-testid="unmatch-button"
            ghost
            className="border-none shadow-none"
          >
            {t('resource-mapper.undo')}
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      width="80vw"
      style={{ top: 0 }}
      footer={null}
      className="matching-history-modal"
    >
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-2">
          <span className="flex-1 text-neutral-100 text-20 font-bold">
            {t('resource-mapper.matching_history')}
          </span>
          <p className="text-14 text-neutral-60">{t('resource-mapper.matching_history_detail')}</p>
        </div>

        <Table
          columns={columns}
          dataSource={data}
          locale={isLoading ? undefined : { emptyText: emptyOrNotFoundComponent }}
          loading={{
            indicator: <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />,
            spinning: isLoading,
          }}
          onChange={(_pagination, _filters, sorter) => {
            const field = (sorter as SorterResult<MatchingHistoryItem>)?.columnKey;
            const order = (sorter as SorterResult<MatchingHistoryItem>)?.order;
            if (field && order) {
              reload({ sort: { field, order } });
            }
          }}
          sortDirections={['ascend', 'descend', 'ascend']} // prevent the default state (without sorting)
          pagination={false}
          sticky
          className="w-full connect-table"
          rowKey="mapping_id"
          data-testid="matching_history_table"
          scroll={{ y: '60vh', x: '100%' }}
        />
        <div className="flex justify-between items-center">
          <p className="text-14 text-neutral-60" data-testid="matching_history_count">
            {t('resource-mapper.results', {
              totalResults: data.length,
            })}
          </p>
          <Button
            size="large"
            ghost
            disabled={!hasMore || isLoading}
            onClick={() => {
              trackEvent(AmplitudeEventEnum.LOAD_MORE_MATCHED_PRODUCTS);
              loadMore(orgId);
            }}
            data-testid="load_more_button"
          >
            {t('resource-mapper.load_more')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default MatchingHistoryModal;
